/**
 * Pager Component
 * Handles pagination logic and smart page number calculation
 */

/**
 * Calculate visible page numbers with smart ellipsis placement
 * @param {number} currentPage - Current active page (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {number} maxVisible - Maximum number of page buttons to show
 * @returns {Array} Array of page objects with type and number properties
 */
export function getVisiblePages(currentPage, totalPages, maxVisible = 8) {
	// If total pages less than or equal to max visible, show all pages
	if (totalPages <= maxVisible) {
		return Array.from({ length: totalPages }, (_, i) => ({
			type: "page",
			number: i + 1,
		}));
	}

	const pages = [];
	const sidePages = 2; // Number of pages to show on each side of current page

	// Always include first page
	pages.push({ type: "page", number: 1 });

	// Calculate range around current page
	const leftBound = Math.max(2, currentPage - sidePages);
	const rightBound = Math.min(totalPages - 1, currentPage + sidePages);

	// Add ellipsis after first page if needed
	if (leftBound > 2) {
		pages.push({ type: "ellipsis" });
	}

	// Add pages around current page
	for (let i = leftBound; i <= rightBound; i++) {
		pages.push({ type: "page", number: i });
	}

	// Add ellipsis before last page if needed
	if (rightBound < totalPages - 1) {
		pages.push({ type: "ellipsis" });
	}

	// Always include last page if more than 1 page
	if (totalPages > 1) {
		pages.push({ type: "page", number: totalPages });
	}

	return pages;
}

/**
 * Calculate visible page numbers for mobile with fewer visible pages
 * @param {number} currentPage - Current active page (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @returns {Array} Array of page objects with type and number properties
 */
export function getVisiblePagesMobile(currentPage, totalPages) {
	return getVisiblePages(currentPage, totalPages, 5);
}

/**
 * Initialize pager component with progressive enhancement
 */
export function initPager() {
	const pagers = document.querySelectorAll(".Pager");

	pagers.forEach((pager) => {
		// Get data attributes
		const currentPage = parseInt(pager.dataset.currentPage, 10);
		const totalPages = parseInt(pager.dataset.totalPages, 10);

		// Only process pages variant
		if (!pager.classList.contains("Pager--pages")) {
			return;
		}

		// Dispatch custom event when pager is clicked
		pager.addEventListener("click", (event) => {
			const button = event.target.closest(".Pager-pageButton, .Pager-button");

			if (
				!button ||
				button.classList.contains("Pager-button--disabled") ||
				button.classList.contains("Pager-pageButton--current")
			) {
				return;
			}

			// Get target page
			let targetPage = currentPage;

			if (button.classList.contains("Pager-button--prev")) {
				targetPage = currentPage - 1;
			} else if (button.classList.contains("Pager-button--next")) {
				targetPage = currentPage + 1;
			} else if (button.classList.contains("Pager-pageButton")) {
				targetPage = parseInt(button.textContent, 10);
			}

			// Dispatch custom event for apps to handle
			const pageChangeEvent = new CustomEvent("pager:change", {
				detail: {
					currentPage,
					targetPage,
					totalPages,
				},
				bubbles: true,
				cancelable: true,
			});

			const shouldNavigate = pager.dispatchEvent(pageChangeEvent);

			// If event was prevented, stop default navigation
			if (!shouldNavigate) {
				event.preventDefault();
			}
		});
	});
}

// Auto-initialize on DOM ready
if (typeof document !== "undefined") {
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initPager);
	} else {
		initPager();
	}
}

export default {
	getVisiblePages,
	getVisiblePagesMobile,
	initPager,
};
